import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Pencil,
  Send,
  Archive,
  Copy,
  Trash2,
  Clock,
  Users,
  BarChart3,
  HelpCircle,
  Shield,
  Settings,
  Eye,
  ListChecks,
  Calendar,
  Tag,
  RefreshCcw,
  AlertCircle,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, AppButton, ConfirmModal, EmptyState } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./AssessmentDetailsV2.scss";

const MODE_LABELS = {
  practice: "Practice",
  exam: "Exam",
  sectioned_exam: "Sectioned Exam",
};
const STATUS_LABELS = { draft: "Draft", published: "Published", archived: "Archived" };
const QUESTION_TYPE_LABELS = {
  single_choice: "Single Choice",
  multi_select: "Multi Select",
  true_false: "True / False",
  text_answer: "Text",
  fill_blank: "Fill Blank",
  image_mcq: "Image MCQ",
  code_conceptual: "Code",
};
const DIFFICULTY_LABELS = { easy: "Easy", medium: "Medium", hard: "Hard" };

// ─── Small presentational bits ──────────────────────────────────────────
const Pill = ({ tone = "muted", children }) => (
  <span className={`adv-pill adv-pill--${tone}`}>{children}</span>
);

const StatTile = ({ label, value, icon: Icon, tone = "blue" }) => (
  <div className={`adv-stat adv-stat--${tone}`}>
    <div className="adv-stat-head">
      <span className="adv-stat-label">{label}</span>
      {Icon && <Icon size={16} className="adv-stat-icon" />}
    </div>
    <div className="adv-stat-value">{value}</div>
  </div>
);

const Section = ({ icon: Icon, title, subtitle, actions, children, muted }) => (
  <section className={`adv-section ${muted ? "adv-section--muted" : ""}`}>
    <header className="adv-section-head">
      <div className="adv-section-title">
        {Icon && <Icon size={16} />}
        <span>{title}</span>
      </div>
      {subtitle && <span className="adv-section-subtitle">{subtitle}</span>}
      {actions && <div className="adv-section-actions">{actions}</div>}
    </header>
    <div className="adv-section-body">{children}</div>
  </section>
);

const DifficultyDot = ({ level }) => (
  <span className={`adv-diff adv-diff--${level}`}>
    <span className="adv-diff-dot" />
    {DIFFICULTY_LABELS[level] || level}
  </span>
);

// ─── Main component ─────────────────────────────────────────────────────
const AssessmentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  const fetchAssessment = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/assessments/${id}`);
      setAssessment(data.assessment);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to load assessment", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePublish = async () => {
    setActionBusy(true);
    try {
      await apiClient.put(`/assessments/${id}/publish`);
      showToast("Assessment published");
      await fetchAssessment();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to publish", "error");
    } finally {
      setActionBusy(false);
      setPublishConfirm(false);
    }
  };

  const handleArchive = async () => {
    setActionBusy(true);
    try {
      await apiClient.put(`/assessments/${id}/archive`);
      showToast("Assessment archived");
      await fetchAssessment();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to archive", "error");
    } finally {
      setActionBusy(false);
      setArchiveConfirm(false);
    }
  };

  const handleDuplicate = async () => {
    setActionBusy(true);
    try {
      const { data } = await apiClient.post(`/assessments/${id}/duplicate`);
      showToast("Duplicated");
      setTimeout(() => navigate(`/assessments/${data.assessment._id}`), 500);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to duplicate", "error");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async () => {
    setActionBusy(true);
    try {
      await apiClient.delete(`/assessments/${id}`);
      showToast("Deleted");
      setTimeout(() => navigate("/assessments"), 500);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete", "error");
    } finally {
      setActionBusy(false);
      setDeleteConfirm(false);
    }
  };

  const totalMarks = useMemo(() => {
    if (!assessment) return 0;
    if (assessment.selectionMode === "manual") {
      return (assessment.questions || []).reduce(
        (s, q) => s + (q.overrideMarks ?? q.question?.marks ?? 1),
        0
      );
    }
    // Dynamic: approximate by avg marks * total count (fallback to totalQuestions)
    return assessment.dynamicConfig?.totalQuestions || 0;
  }, [assessment]);

  // ── Loading state ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="assessment-details-v2">
        <PageHeader backButton title="Assessment" />
        <div className="adv-skeleton-hero" />
        <div className="adv-skeleton-stats">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="adv-skeleton-card" />
          ))}
        </div>
        <div className="adv-skeleton-section" />
        <div className="adv-skeleton-section" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="assessment-details-v2">
        <PageHeader backButton title="Assessment" />
        <EmptyState
          icon={<AlertCircle size={40} />}
          title="Assessment not found"
          description="It may have been deleted or you don't have access."
          action={
            <AppButton variant="outline" onClick={() => navigate("/assessments")}>
              Back to list
            </AppButton>
          }
        />
      </div>
    );
  }

  const a = assessment;
  const statusTone =
    a.status === "published" ? "success" : a.status === "archived" ? "warn" : "muted";
  const canDelete = (a.totalAttempts || 0) === 0;

  // ── Header actions ──────────────────────────────────────────────────
  const headerActions = (
    <div className="adv-header-actions">
      <AppButton
        variant="outline"
        size="sm"
        onClick={() => navigate(`/assessments/edit/${id}`)}
      >
        <Pencil size={14} /> <span>Edit</span>
      </AppButton>
      <AppButton
        variant="outline"
        size="sm"
        onClick={handleDuplicate}
        disabled={actionBusy}
      >
        <Copy size={14} /> <span>Duplicate</span>
      </AppButton>
      {a.status === "draft" && (
        <AppButton
          variant="default"
          size="sm"
          onClick={() => setPublishConfirm(true)}
          disabled={actionBusy}
        >
          <Send size={14} /> <span>Publish</span>
        </AppButton>
      )}
      {a.status === "published" && (
        <AppButton
          variant="secondary"
          size="sm"
          onClick={() => setArchiveConfirm(true)}
          disabled={actionBusy}
        >
          <Archive size={14} /> <span>Archive</span>
        </AppButton>
      )}
      {canDelete && (
        <AppButton
          variant="destructive"
          size="icon"
          onClick={() => setDeleteConfirm(true)}
          disabled={actionBusy}
          title="Delete assessment"
          aria-label="Delete assessment"
        >
          <Trash2 size={14} />
        </AppButton>
      )}
      <AppButton
        variant="ghost"
        size="icon"
        onClick={fetchAssessment}
        disabled={actionBusy || loading}
        title="Refresh"
        aria-label="Refresh"
      >
        <RefreshCcw size={14} />
      </AppButton>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="assessment-details-v2">
      <PageHeader
        backButton
        title={a.title}
        description={a.description}
        actions={headerActions}
      />

      {/* Meta chip row */}
      <div className="adv-meta-row">
        <Pill tone={statusTone}>{STATUS_LABELS[a.status]}</Pill>
        <Pill tone="info">{MODE_LABELS[a.mode]}</Pill>
        <Pill tone="purple">
          {a.selectionMode === "dynamic" ? "Dynamic selection" : "Manual selection"}
        </Pill>
        {a.publishedAt && (
          <span className="adv-meta-date">
            <Calendar size={12} /> Published{" "}
            {new Date(a.publishedAt).toLocaleDateString()}
          </span>
        )}
        {a.updatedAt && (
          <span className="adv-meta-date">
            <RefreshCcw size={12} /> Updated{" "}
            {new Date(a.updatedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Stat tiles */}
      <div className="adv-stats">
        <StatTile
          label="Questions"
          value={a.questionCount || 0}
          icon={HelpCircle}
          tone="blue"
        />
        <StatTile
          label="Total Marks"
          value={totalMarks}
          icon={BarChart3}
          tone="green"
        />
        <StatTile
          label="Time Limit"
          value={a.timeLimit > 0 ? `${a.timeLimit}m` : "No limit"}
          icon={Clock}
          tone="orange"
        />
        <StatTile
          label="Attempts"
          value={a.totalAttempts || 0}
          icon={Users}
          tone="pink"
        />
      </div>

      {/* Two column layout */}
      <div className="adv-body">
        {/* Main column */}
        <div className="adv-main">
          {a.instructions && (
            <Section icon={Eye} title="Instructions">
              <p className="adv-instructions">{a.instructions}</p>
            </Section>
          )}

          {/* Manual question list */}
          {a.selectionMode === "manual" && a.questions?.length > 0 && (
            <Section
              icon={ListChecks}
              title="Questions"
              subtitle={`${a.questions.length} total`}
            >
              <div className="adv-table-wrap">
                <table className="adv-table">
                  <thead>
                    <tr>
                      <th className="num-col">#</th>
                      <th>Question</th>
                      <th>Type</th>
                      <th>Difficulty</th>
                      <th className="num-col">Marks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.questions.map((qi, idx) => {
                      const q = qi.question;
                      return (
                        <tr key={q?._id || idx}>
                          <td className="num-col">{idx + 1}</td>
                          <td className="q-text">
                            {q?.questionText || "—"}
                          </td>
                          <td>
                            <span className="adv-chip">
                              {QUESTION_TYPE_LABELS[q?.questionType] ||
                                q?.questionType}
                            </span>
                          </td>
                          <td>
                            {q?.difficulty && <DifficultyDot level={q.difficulty} />}
                          </td>
                          <td className="num-col">
                            <strong>
                              {qi.overrideMarks ?? q?.marks ?? 1}
                            </strong>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {a.selectionMode === "manual" && (!a.questions || a.questions.length === 0) && (
            <Section icon={ListChecks} title="Questions">
              <EmptyState
                icon={<ListChecks size={40} />}
                title="No questions added yet"
                description="Edit this assessment to add questions."
                action={
                  <AppButton
                    variant="outline"
                    onClick={() => navigate(`/assessments/edit/${id}`)}
                  >
                    <Pencil size={14} /> <span>Edit to add questions</span>
                  </AppButton>
                }
              />
            </Section>
          )}

          {/* Dynamic rules */}
          {a.selectionMode === "dynamic" && (
            <Section
              icon={Settings}
              title="Dynamic Selection Rules"
              subtitle={`${a.dynamicConfig?.totalQuestions || 0} questions per attempt`}
            >
              {a.dynamicConfig?.rules?.length ? (
                <div className="adv-table-wrap">
                  <table className="adv-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Subcategory</th>
                        <th>Difficulty</th>
                        <th>Tags</th>
                        <th className="num-col">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.dynamicConfig.rules.map((r) => (
                        <tr
                          key={
                            r._id ||
                            `${r.category?._id || "any"}-${r.difficulty || "any"}-${r.count}`
                          }
                        >
                          <td>{r.category?.name || <em className="muted">Any</em>}</td>
                          <td>
                            {r.subcategory?.name || <em className="muted">Any</em>}
                          </td>
                          <td>
                            {r.difficulty ? (
                              <DifficultyDot level={r.difficulty} />
                            ) : (
                              <em className="muted">Any</em>
                            )}
                          </td>
                          <td>
                            {r.tags?.length ? (
                              <div className="adv-tag-list">
                                {r.tags.map((t) => (
                                  <span key={t} className="adv-chip adv-chip--tag">
                                    {t}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <em className="muted">—</em>
                            )}
                          </td>
                          <td className="num-col">
                            <strong>{r.count}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  icon={<Settings size={40} />}
                  title="No rules configured"
                  description="Edit this assessment to add dynamic selection rules."
                />
              )}
              {a.dynamicConfig?.avoidRecentDays > 0 && (
                <div className="adv-note">
                  Questions used in the last{" "}
                  <strong>{a.dynamicConfig.avoidRecentDays}</strong> days will be
                  skipped.
                </div>
              )}
            </Section>
          )}

          {/* Sectioned exam structure */}
          {a.isSectioned && a.sections?.length > 0 && (
            <Section
              icon={ListChecks}
              title="Sections"
              subtitle={`${a.sections.length} section${a.sections.length === 1 ? "" : "s"}`}
            >
              <ol className="adv-section-list">
                {a.sections.map((s, idx) => {
                  const qCount = s.dynamicConfig?.totalQuestions ?? (s.questions?.length || 0);
                  return (
                    <li key={s._id || idx} className="adv-section-item">
                      <div className="adv-section-item-head">
                        <span className="adv-section-item-index">{idx + 1}</span>
                        <div>
                          <div className="adv-section-item-title">{s.title}</div>
                          {s.description && (
                            <p className="adv-section-item-desc">{s.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="adv-section-item-meta">
                        <span>
                          <HelpCircle size={12} /> {qCount} questions
                        </span>
                        {s.timeLimit > 0 && (
                          <span>
                            <Clock size={12} /> {s.timeLimit}m
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </Section>
          )}
        </div>

        {/* Side column */}
        <aside className="adv-side">
          <Section icon={Settings} title="Settings">
            <dl className="adv-kv">
              <div>
                <dt>Passing %</dt>
                <dd>
                  {a.passingPercentage > 0 ? `${a.passingPercentage}%` : "—"}
                </dd>
              </div>
              <div>
                <dt>Max Attempts</dt>
                <dd>{a.maxAttempts > 0 ? a.maxAttempts : "Unlimited"}</dd>
              </div>
              <div>
                <dt>Negative Marking</dt>
                <dd>{a.negativeMarking ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt>Shuffle Questions</dt>
                <dd>{a.shuffleQuestions ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt>Shuffle Options</dt>
                <dd>{a.shuffleOptions ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt>Instant Results</dt>
                <dd>{a.showResultInstantly ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt>Explanations</dt>
                <dd>{a.showExplanation ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt>Show Correct</dt>
                <dd>{a.showCorrectAnswers ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt>Allow Review</dt>
                <dd>{a.allowReview ? "Yes" : "No"}</dd>
              </div>
            </dl>
          </Section>

          {(a.mode === "exam" || a.mode === "sectioned_exam") && a.antiCheat && (
            <Section icon={Shield} title="Anti-Cheat">
              <dl className="adv-kv">
                <div>
                  <dt>Tab Detection</dt>
                  <dd>
                    {a.antiCheat.tabSwitchDetection
                      ? `Yes (max ${a.antiCheat.maxTabSwitches})`
                      : "No"}
                  </dd>
                </div>
                <div>
                  <dt>Fullscreen</dt>
                  <dd>{a.antiCheat.fullscreenRequired ? "Required" : "No"}</dd>
                </div>
                <div>
                  <dt>Right Click</dt>
                  <dd>{a.antiCheat.disableRightClick ? "Disabled" : "Allowed"}</dd>
                </div>
                <div>
                  <dt>Copy/Paste</dt>
                  <dd>{a.antiCheat.disableCopyPaste ? "Disabled" : "Allowed"}</dd>
                </div>
                <div>
                  <dt>Auto-Submit</dt>
                  <dd>{a.antiCheat.autoSubmitOnViolation ? "Yes" : "No"}</dd>
                </div>
              </dl>
            </Section>
          )}

          <Section icon={Users} title="Assignment">
            <dl className="adv-kv">
              <div>
                <dt>Type</dt>
                <dd className="capitalise">{a.assignmentType || "—"}</dd>
              </div>
              {a.assignedBatches?.length > 0 && (
                <div>
                  <dt>Batches</dt>
                  <dd>
                    <div className="adv-tag-list">
                      {a.assignedBatches.map((b, i) => (
                        <span key={b?._id || i} className="adv-chip">
                          {b?.name || b}
                        </span>
                      ))}
                    </div>
                  </dd>
                </div>
              )}
              {a.assignedCourses?.length > 0 && (
                <div>
                  <dt>Courses</dt>
                  <dd>
                    <div className="adv-tag-list">
                      {a.assignedCourses.map((c, i) => (
                        <span key={c?._id || i} className="adv-chip">
                          {c?.title || c?.name || c}
                        </span>
                      ))}
                    </div>
                  </dd>
                </div>
              )}
              {a.startDate && (
                <div>
                  <dt>Start</dt>
                  <dd>{new Date(a.startDate).toLocaleString()}</dd>
                </div>
              )}
              {a.endDate && (
                <div>
                  <dt>End</dt>
                  <dd>{new Date(a.endDate).toLocaleString()}</dd>
                </div>
              )}
            </dl>
          </Section>

          <Section icon={Tag} title="Metadata">
            <dl className="adv-kv">
              <div>
                <dt>Created</dt>
                <dd>
                  {a.createdAt
                    ? new Date(a.createdAt).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>Last Updated</dt>
                <dd>
                  {a.updatedAt
                    ? new Date(a.updatedAt).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>Average Score</dt>
                <dd>
                  {a.averageScore > 0
                    ? `${a.averageScore.toFixed(1)}%`
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>ID</dt>
                <dd className="mono">{a._id}</dd>
              </div>
            </dl>
          </Section>
        </aside>
      </div>

      {/* Modals */}
      <ConfirmModal
        show={publishConfirm}
        onHide={() => setPublishConfirm(false)}
        onConfirm={handlePublish}
        title="Publish assessment?"
        message="Once published, students will be able to see and attempt this assessment."
        confirmText="Publish"
        variant="primary"
      />
      <ConfirmModal
        show={archiveConfirm}
        onHide={() => setArchiveConfirm(false)}
        onConfirm={handleArchive}
        title="Archive assessment?"
        message="Students will no longer see this assessment. You can restore it later."
        confirmText="Archive"
        variant="warning"
      />
      <ConfirmModal
        show={deleteConfirm}
        onHide={() => setDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete assessment?"
        message="This will permanently delete the assessment. This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default AssessmentDetailsPage;
